/* tslint:disable:max-line-length */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/switchMap';
import {
  LocationSummary,
  SetupChangeDeleteValidationResult,
  SetupChangeLogRecord,
  SetupChangeType,
  SetupMetadata,
  StageStartAndEnd,
  UpdatedSetupSummary,
} from '../../models/setup/setup.models';
import { DbContext, DbTable } from '../data/dbcontext.service';
import { DateService } from '../date/date.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../models/projects/date.models';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { OperationService } from '../operations/operation/operation.service';
import { cloneDeep } from 'lodash';
import { TimeZoneChangeEditor } from '../../models/time-zones/time-zone.models';
import { AppConfig } from '../../app/app.config';
import { DateTime } from 'luxon';
import { SetupDataService } from './setup-data.service';
import { AppData } from '../../models/app-data/app-data';
import { DailyOperationLogRecord } from '../../models/projects/operation.models';

@Injectable()
export class SetupService {
  config = AppConfig.settings;
  private readonly setupTable: DbTable;
  private readonly setupChangeLogTable: DbTable;

  constructor(
    private http: HttpClient,
    private dbContext: DbContext,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private timeZoneService: TimeZoneService,
    private operationService: OperationService,
    private setupDataService: SetupDataService
  ) {
    this.setupTable = this.dbContext.tables.find(t => t.name === 'Setup');
    this.setupChangeLogTable = this.dbContext.tables.find(t => t.name === 'SetupChangeLogData');
  }

  // leaving this method here as many components already inject the setup service, so dont want to inject the data
  // service in all places this is needed
  async getSetupChangeLogData(): Promise<SetupChangeLogRecord[]> {
    return await this.setupDataService.getSetupChangeLogData();
  }

  checkInitialSetupComplete(): Promise<boolean> {
    const url = `${this.config.apiUrl}api/setup/initialsetupcomplete`;

    return this.http.get<boolean>(url).toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(false);
      });
  }

  async getSetupMetadata(vesselId: number, dailyLogDate: DateTime, initSetup: boolean = false): Promise<SetupMetadata> {
    let url = '';

    if (dailyLogDate) {
      if (initSetup) {
        dailyLogDate = dailyLogDate.setZone('utc', {keepLocalTime: true});
      }

      const dateLong = dailyLogDate.toMillis();

      url = `${this.config.apiUrl}api/projects/metadata/${vesselId}/${dateLong}/${initSetup}`;
    } else {
      url = `${this.config.apiUrl}api/projects/metadata/${vesselId}}`;
    }

    return this.http.get<SetupMetadata>(url).toPromise()
      .then(setupMetaData => {
        const result = Promise.resolve(setupMetaData);

        const table = this.dbContext.tables.find(t => t.name === 'Metadata');
        this.dbContext.addOrUpdate(table, 1, result);

        return result;
      });
  }

  async getLocalSetupSummary(): Promise<UpdatedSetupSummary> {
    const editor = await this.dbContext.get(this.setupTable, 1) as UpdatedSetupSummary;

    if (!!editor) {
      editor.locationTimeChanged = PtLuxonDate.assignPtLuxonDate(editor.locationTimeChanged);
      editor.stageTimeChanged = PtLuxonDate.assignPtLuxonDate(editor.stageTimeChanged);
      editor.phaseTimeChanged = PtLuxonDate.assignPtLuxonDate(editor.phaseTimeChanged);
      editor.projectTimeChanged = PtLuxonDate.assignPtLuxonDate(editor.projectTimeChanged);
      editor.vesselMasterTimeChanged = PtLuxonDate.assignPtLuxonDate(editor.vesselMasterTimeChanged);
    }

    return editor;
  }

  async getTimesForStage(id: number, setupData?: SetupChangeLogRecord[]): Promise<StageStartAndEnd[]> {
    let data = setupData ? setupData : await this.setupDataService.getSetupChangeLogData();

    data = data.filter(e => e.timeOfChange && e.timeOfChange.dateTime);

    data = data.sort((a, b) => a.timeOfChange.dateTime.toMillis() - b.timeOfChange.dateTime.toMillis());

    let startRecords = data.filter(p => p.newValueId === id && p.changeTypeId === 3);
    const endRecords = data.filter(p => p.previousValueId === id && p.changeTypeId === 3);
    const stageTimes = [] as StageStartAndEnd[];

    // startRecords = setupChanges where the newValue == id, i.e user is now moving into the chosen stage
    // endRecords = setupChanges where the oldValue == id, i.e user is changing from the chosen stage to a different stage
    for (const endRecord of endRecords) {
      const stageTime = new StageStartAndEnd();
      const matchingStartRecord = startRecords.find(p => p.newValueId === endRecord.previousValueId && p.timeOfChange.dateTime < endRecord.timeOfChange.dateTime);

      if (matchingStartRecord) {
        startRecords = startRecords.filter(p => p.id !== matchingStartRecord.id);

        stageTime.stageId = endRecord.previousValueId;
        stageTime.endTime = endRecord.timeOfChange;
        stageTime.startTime = matchingStartRecord.timeOfChange;
        stageTimes.push(stageTime);
      }
    }

    const finalStartRecord = startRecords.find(p => p.newValueId === id);

    if (!!finalStartRecord) {
      const stageTime = new StageStartAndEnd();
      stageTime.stageId = finalStartRecord.previousValueId;
      stageTime.endTime = null;
      stageTime.startTime = finalStartRecord.timeOfChange;
      stageTimes.push(stageTime);
    }

    return stageTimes;
  }

  assignEditorFromSummary(updatedSummary: UpdatedSetupSummary, editor?: UpdatedSetupSummary): UpdatedSetupSummary {
    if (editor === null) {
      editor = new UpdatedSetupSummary();
    }

    editor.projectId = updatedSummary.projectId ? updatedSummary.projectId : editor.projectId;
    editor.projectName = updatedSummary.projectName ? updatedSummary.projectName : editor.projectName;
    editor.projectTimeChanged = updatedSummary.projectTimeChanged ? updatedSummary.projectTimeChanged : editor.projectTimeChanged;

    editor.phaseId = updatedSummary.phaseId ? updatedSummary.phaseId : editor.phaseId;
    editor.phaseName = updatedSummary.phaseName ? updatedSummary.phaseName : editor.phaseName;
    editor.phaseTimeChanged = updatedSummary.phaseTimeChanged ? updatedSummary.phaseTimeChanged : editor.phaseTimeChanged;

    editor.stageId = updatedSummary.stageId ? updatedSummary.stageId : editor.stageId;
    editor.stageName = updatedSummary.stageName ? updatedSummary.stageName : editor.stageName;
    editor.stageTimeChanged = updatedSummary.stageTimeChanged ? updatedSummary.stageTimeChanged : editor.stageTimeChanged;

    editor.locationId = updatedSummary.locationId ? updatedSummary.locationId : editor.locationId;
    editor.locationName = updatedSummary.locationName ? updatedSummary.locationName : editor.locationName;
    editor.locationTimeChanged = updatedSummary.locationTimeChanged ? updatedSummary.locationTimeChanged : editor.locationTimeChanged;
    editor.selectedLocation = {
      id: editor.locationId,
      name: editor.locationName
    }
    editor.vesselMasterName = updatedSummary.vesselMasterName;
    editor.vesselMasterTimeChanged = updatedSummary.vesselMasterTimeChanged;

    editor.clientRepName = updatedSummary.clientRepName;
    editor.clientRepTitle = updatedSummary.clientRepTitle;
    editor.otherRepName = updatedSummary.otherRepName;
    editor.otherRepTitle = updatedSummary.otherRepTitle;

    return editor;
  }

  async getSetupSummary(date: PtLuxonDate, useEndOfDay: boolean): Promise<UpdatedSetupSummary> {
    const chosenDate = date.dateTime;

    if (!chosenDate) {
      return new UpdatedSetupSummary;
    }

    const url = `${this.config.apiUrl}api/appdata/${chosenDate.valueOf()}/setupsummary/${useEndOfDay}`;
    const result = await this.http.get<UpdatedSetupSummary>(url).toPromise();
    return await this.dateParserService.deserializeDates(result, true, this.timeZoneService);
  }

  deleteSetupChangeLogRecord(id: number): Observable<any> {
    const url = `${this.config.apiUrl}api/appdata/${id}/deletesetupchangerecord`;

    return this.http.delete(url);
  }

  async deleteLocalSetupChangeLogRecord(id: number) {
    await this.dbContext.remove(this.setupChangeLogTable, id);
  }

  async getTransitsBetweenTimes(startDate: PtLuxonDate, endDate: PtLuxonDate, inclusive?: boolean): Promise<SetupChangeLogRecord[]> {
    const logData = await this.setupDataService.getSetupChangeLogData();

    let transitRecords = logData.filter(e => e.changeTypeId === SetupChangeType.Transiting);

    // only includes records where the startTime matches the timeOfChange when inclusive
    if (inclusive) {
      transitRecords = transitRecords.filter(e =>
        e.timeOfChange.dateTime >= startDate.dateTime &&
        e.timeOfChange.dateTime < endDate.dateTime)
        .sort((a, b) => this.sortSetupChangeLogAsc(a, b));
    } else {
      transitRecords = transitRecords.filter(e =>
        e.timeOfChange.dateTime > startDate.dateTime &&
        e.timeOfChange.dateTime < endDate.dateTime)
        .sort((a, b) => this.sortSetupChangeLogAsc(a, b));
    }


    for (let i = 0; i < transitRecords.length; i++) {
      if (transitRecords[i].timeOfChange.dateTime.toMillis() === startDate.dateTime.toMillis()) {
        transitRecords[i].startingTransit = true;
      }
    }

    return transitRecords;
  }

  // return the location that is considered the active location for the time given
  async getLocationForTime(localDate: DateTime, includeTransiting: boolean = false): Promise<SetupChangeLogRecord> {
    const logData = await this.setupDataService.getSetupChangeLogData();

    const location = logData
      .filter(e => e.timeOfChange.dateTime <= localDate && (includeTransiting
        ? (e.changeTypeId === SetupChangeType.Transiting || e.changeTypeId === SetupChangeType.LocationChange)
        : e.changeTypeId === SetupChangeType.LocationChange))
      .sort((a, b) => this.sortSetupChangeLogDesc(a, b));

    // if location has 2 items, it means one is a location change and one is a transiting to change
    // in this case, ignore the transiting - this fixes issue on op-details-edit-location-select.ts where the previous op would be incorrect
    // Following code removed by Ryan on 28/04/20 - Leaving James comment above for now in case this is wrong.
    // if (location && location.length > 1 && location[0].timeOfChange.dateTime.toMillis() === location[1].timeOfChange.dateTime.toMillis()) {
    //   location = location.filter(e => e.changeTypeId !== SetupChangeType.Transiting);
    // }

    return location[0];
  }

  async futureLocationChangeExists(ptDate: PtLuxonDate): Promise<boolean> {
    const nextLocation = await this.getNextLocationChange(ptDate);

    if (nextLocation == null) {
      return false;
    }

    return true;
  }

  async getNextLocationChange(ptLuxonDate: PtLuxonDate): Promise<SetupChangeLogRecord> {
    if (!ptLuxonDate || !ptLuxonDate.dateTime) {
      return null;
    }

    const logData = await this.setupDataService.getSetupChangeLogData();

    const location = logData
      .filter(e => e.changeTypeId === SetupChangeType.LocationChange && e.timeOfChange.dateTime.toMillis() > ptLuxonDate.dateTime.toMillis())
      .sort((a, b) => this.sortSetupChangeLogDesc(a, b));

    return location[location.length - 1];
  }

  saveSetup(summary: UpdatedSetupSummary) {
    return this.dbContext.addOrUpdate(this.setupTable, 1, summary);
  }

  getSetupChangeLogRecordTempId(): Promise<number> {
    return this.dbContext.getTempId(this.setupChangeLogTable).then(id => {
      return <number>id;
    });
  }

  getSetupChangeLogRecord(id: number): Promise<SetupChangeLogRecord> {
    return this.dbContext.get(this.setupChangeLogTable, id).then(record => {
      return record;
    });
  }

  saveSetupChangeLogRecord(id: number, setupChange: SetupChangeLogRecord): Promise<any> {
    return this.dbContext.addOrUpdate(this.setupChangeLogTable, <number>id, setupChange);
  }

  async saveSetupChangeMultipleRecords(setupChangeRecords: SetupChangeLogRecord[]) {
    for (let i = 0; i < setupChangeRecords.length; i++) {
      await this.dbContext.addOrUpdate(this.setupChangeLogTable, setupChangeRecords[i].id, setupChangeRecords[i]);
    }
  }

  deleteAllSetupChangeLogRecords() {
    return this.dbContext.removeAll(this.setupChangeLogTable);
  }

  // returns the setupChangeLogRecord that was active when at the time of 'dateOfChange'
  async getPreviousSetupChangeLogRecord(dateOfChange: PtLuxonDate, changeTypeId: SetupChangeType): Promise<SetupChangeLogRecord> {
    const logData = await this.setupDataService.getSetupChangeLogData();

    const record = logData
      .filter(e => e.changeTypeId === changeTypeId && e.timeOfChange.dateTime.toMillis() < dateOfChange.dateTime.toMillis())
      .sort((a, b) => this.sortSetupChangeLogDesc(a, b));

    return record[0];
  }

  // gets all records with the same batch id as the given id, except the record for the given record id
  async getSetupChangeLogDataBatchRecords(recordId: number, batchId: string): Promise<SetupChangeLogRecord[]> {
    let instances = await this.setupDataService.getSetupChangeLogData() as SetupChangeLogRecord[];

    return instances.filter(e => e.batchId === batchId && e.id !== recordId && e.localId !== recordId);
  }

  async updateSetupChangeLogDataForDate(date: DateTime) {
    const url = `${this.config.apiUrl}api/setup/getsetupdatafordate/` + date.toMillis();
    const data = await this.http.get<SetupChangeLogRecord[]>(url).toPromise();
    await this.saveSetupChangeMultipleRecords(data);
  }

  async getSetupChangeLogDataForDay(date: PtLuxonDate): Promise<SetupChangeLogRecord[]> {
    let instances = await this.setupDataService.getSetupChangeLogData() as SetupChangeLogRecord[];

    if (!date) {
      return [];
    }

    if (!instances || instances.length === 0) {
      await this.updateSetupChangeLogDataForDate(date.dateTime);
      instances = await this.setupDataService.getSetupChangeLogData() as SetupChangeLogRecord[];
    }

    const filteredInstances: SetupChangeLogRecord[] = [];

    for (let i = 0; i < instances.length; i++) {
      const sameDay = this.dateService
        .compareDayOffsetsPreApplied(instances[i].timeOfChange.dateTime, date.dateTime);
      if (sameDay) {
        filteredInstances.push(instances[i]);
      }
    }

    return filteredInstances;
  }

  private sortSetupChangeLogDesc(a: SetupChangeLogRecord, b: SetupChangeLogRecord) {
    if (a.timeOfChange.dateTime > b.timeOfChange.dateTime) {
      return -1;
    }
    if (a.timeOfChange.dateTime < b.timeOfChange.dateTime) {
      return 1;
    }

    // transiting and location change may have the same time, transiting should get priority order
    if (a.changeTypeId === SetupChangeType.Transiting && b.changeTypeId === SetupChangeType.LocationChange) {
      return -1;
    }

    return 0;
  }

  private sortSetupChangeLogAsc(a: SetupChangeLogRecord, b: SetupChangeLogRecord) {
    if (a.timeOfChange.dateTime > b.timeOfChange.dateTime) {
      return 1;
    }
    if (a.timeOfChange.dateTime < b.timeOfChange.dateTime) {
      return -1;
    }

    // transiting and location change may have the same time, transiting should get priority order
    if (a.changeTypeId === SetupChangeType.Transiting && b.changeTypeId === SetupChangeType.LocationChange) {
      return -1;
    }

    return 0;
  }

  // return the location that is considered the active location for the time given
  // excludedLocationId needed to ensure the correct location gets returned when adding an operation to the end of a previous rig move
  // because the operation isn't yet saved, the previous location setup record at the end of the previous rig move has not yet been moved,
  async getSetupLocationForTime(ptDate: PtLuxonDate, excludedLocationId?: number): Promise<SetupChangeLogRecord> {
    if (!ptDate || !ptDate.dateTime) {
      return null;
    }

    let data = await this.setupDataService.getSetupChangeLogData() as SetupChangeLogRecord[];
    data = data
      .filter(e =>
        e.changeTypeId === SetupChangeType.LocationChange
        && (excludedLocationId ? e.newValueId !== excludedLocationId : true)
        && e.timeOfChange.dateTime.toMillis() <= ptDate.dateTime.toMillis())
      .sort((a, b) => {
        if (a.timeOfChange.dateTime < b.timeOfChange.dateTime) {
          return -1;
        }
        if (a.timeOfChange.dateTime > b.timeOfChange.dateTime) {
          return 1;
        }
        return 0;
      });

    return data[data.length - 1];
  }

  canDeleteSetupChange(dailyLogRecordId: number): Observable<SetupChangeDeleteValidationResult> {
    const url = `${this.config.apiUrl}api/setup/candeletesetupchange/${dailyLogRecordId}`;

    return this.http.get<SetupChangeDeleteValidationResult>(url);
  }

  async getClientForProject(projectId: number): Promise<string> {
    const url = `${this.config.apiUrl}api/setup/getclientforproject/${projectId}`;

    return this.http.get<string>(url).toPromise();
  }

  async getLocationSummary(locationId: number): Promise<LocationSummary> {
    const url = `${this.config.apiUrl}api/setup/getlocationsummary/${locationId}`;

    return this.http.get<LocationSummary>(url).toPromise();
  }

  async getNewSetupChangeRecordLocalId() {
    return await this.dbContext.getTempId(this.setupChangeLogTable) as number;
  }
}



// WEBPACK FOOTER //
// ./src/services/setup/setup.service.ts